// src/components/ParlayCard.tsx
import { Parlay, ParlayStatus } from '../domain/types';
import { FiCheckCircle, FiXCircle, FiClock, FiX } from 'react-icons/fi';

const STATUS_CONFIG: Record<ParlayStatus, { label: string; color: string; icon: React.ReactNode }> = {
  [ParlayStatus.PENDING]: { label: 'Pendente', color: '#B7FF00', icon: <FiClock size={14} /> },
  [ParlayStatus.GREEN]: { label: 'Green', color: '#22C55E', icon: <FiCheckCircle size={14} /> },
  [ParlayStatus.RED]: { label: 'Red', color: '#EF4444', icon: <FiXCircle size={14} /> },
  [ParlayStatus.VOID]: { label: 'Void', color: '#888', icon: <FiX size={14} /> },
};

const SPORT_COLOR = { NBA: '#B7FF00', NHL: '#B7FF00' } as const;

interface ParlayCardProps {
  parlay: Parlay;
  onResolve?: () => void;
  onDelete?: () => void;
}

export function ParlayCard({ parlay, onResolve, onDelete }: ParlayCardProps) {
  const status = STATUS_CONFIG[parlay.status];
  const createdAt = new Date(parlay.createdAt).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

  return (
    <div style={{
      background: 'rgba(255,255,255,0.02)',
      border: `1px solid ${parlay.status === ParlayStatus.PENDING ? 'rgba(234,179,8,0.3)' : 'rgba(255,255,255,0.06)'}`,
      borderRadius: 16,
      padding: 16,
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            width: 28, height: 28, borderRadius: 8,
            background: `linear-gradient(135deg, ${status.color}22, ${status.color}11)`,
            border: `1px solid ${status.color}44`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>{status.icon}</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14 }}>Parlay {parlay.picks.length} pernas</div>
            <div style={{ fontSize: 11, color: '#888' }}>{createdAt} • Odd {parlay.combinedOdds.toFixed(2)}x</div>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
            background: `${status.color}22`, color: status.color, border: `1px solid ${status.color}44`,
          }}>
            {status.icon} {status.label}
          </div>
        </div>
      </div>

      {/* Legs */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
        {parlay.picks.map((leg, i) => (
          <div key={leg.id} style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '10px 12px', background: 'rgba(255,255,255,0.02)',
            borderRadius: 10, border: '1px solid rgba(255,255,255,0.04)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
              <span style={{
                width: 22, height: 22, borderRadius: '50%',
                background: SPORT_COLOR[leg.sport],
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 10, fontWeight: 700, color: '#000', flexShrink: 0,
              }}>{i + 1}</span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {leg.player}
                </div>
                <div style={{ fontSize: 11, color: '#888', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <span>{leg.sport}</span>
                  <span style={{ color: leg.side === 'over' ? '#22C55E' : '#EF4444', fontWeight: 600 }}>
                    {leg.side.toUpperCase()} {leg.line}
                  </span>
                  <span>@{leg.odds.toFixed(2)}</span>
                  {leg.hitRatePct && <span style={{ color: leg.hitRatePct >= 60 ? '#22C55E' : '#888' }}>HR {leg.hitRatePct}%</span>}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <div>
          <div style={{ fontSize: 11, color: '#888' }}>Stake</div>
          <div style={{ fontSize: 16, fontWeight: 700 }}>R$ {parlay.stake.toFixed(2)}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 11, color: '#888' }}>Retorno</div>
          <div style={{
            fontSize: 16, fontWeight: 700,
            color: parlay.status === ParlayStatus.GREEN ? '#22C55E'
              : parlay.status === ParlayStatus.RED ? '#EF4444'
              : '#B7FF00',
          }}>
            R$ {parlay.potentialReturn.toFixed(2)}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {parlay.status === ParlayStatus.PENDING && onResolve && (
            <button onClick={onResolve} style={{
              padding: '8px 16px', background: 'rgba(234,179,8,0.2)', border: '1px solid rgba(234,179,8,0.4)',
              borderRadius: 8, color: '#B7FF00', fontSize: 12, fontWeight: 600,
            }}>Resolver</button>
          )}
          {onDelete && (
            <button onClick={onDelete} style={{
              padding: '8px 12px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)',
              borderRadius: 8, color: '#EF4444', fontSize: 12,
            }}>Excluir</button>
          )}
        </div>
      </div>
    </div>
  );
}
